import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../utils/api";
import { MdCheckCircle, MdRadioButtonUnchecked, MdClose, MdZoomIn, MdBolt, MdWifi } from "react-icons/md";
import { jwtDecode } from "jwt-decode";
import toast from "react-hot-toast";

const StandPlanViewer = ({ plan }) => {
  const [fullView, setFullView] = useState(false);

  if (!plan?.file) return null;

  const isPDF = plan.file.match(/\.pdf$/i);
  const isImage = plan.file.match(/\.(jpeg|jpg|png|webp)$/i);

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide flex items-center gap-2">
          <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Stand Plan
        </h3>
        {isImage && (
          <button
            onClick={() => setFullView(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition-all shadow-sm hover:shadow-md"
          >
            <MdZoomIn className="text-base" />
            Full View
          </button>
        )}
      </div>

      {isImage ? (
        <div className="relative rounded-lg overflow-hidden border-2 border-slate-300 shadow-md bg-white cursor-pointer group"
          onClick={() => setFullView(true)}>
          <img
            src={plan.file}
            alt="Stand Plan"
            className="w-full max-h-96 object-contain group-hover:opacity-90 transition-opacity"
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white bg-opacity-90 rounded-full p-3">
              <MdZoomIn className="text-2xl text-indigo-600" />
            </div>
          </div>
        </div>
      ) : isPDF ? (
        <a
          href={plan.file}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-indigo-300 rounded-lg font-semibold text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400 transition-all shadow-sm hover:shadow-md"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
          <span className="text-sm">Open PDF Plan</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      ) : null}

      {/* Full View Modal */}
      {fullView && isImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-[200] p-4"
          onClick={() => setFullView(false)}
        >
          <button
            onClick={() => setFullView(false)}
            className="absolute top-4 right-4 w-12 h-12 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center text-white transition-all z-10"
          >
            <MdClose className="text-2xl" />
          </button>

          <div className="absolute top-4 left-4 bg-black bg-opacity-60 text-white px-4 py-2 rounded-lg text-sm font-medium">
            Stand Plan - Full View
          </div>

          <div className="relative max-w-7xl max-h-full">
            <img
              src={plan.file}
              alt="Stand Plan Full View"
              className="max-w-full max-h-[95vh] object-contain shadow-2xl rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </>
  );
};

const getUserFromToken = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;

  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

const user = getUserFromToken();
const userId = user?.id || null;

const getEvent = () => {
  try {
    const event = localStorage.getItem("event");
    return event;
  } catch (error) {
    console.error("Event retrieval error", error);
    return null;
  }
};

const PhotoGallery = ({ photos, title }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  if (!photos || photos.length === 0) return null;

  return (
    <div className="mt-3">
      {title && <p className="text-xs font-medium text-slate-600 mb-2">{title}</p>}
      <div className="grid grid-cols-3 gap-2">
        {photos.map((photo, idx) => (
          <div
            key={idx}
            className="relative aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity border border-slate-200"
            onClick={() => setSelectedPhoto(photo)}
          >
            <img
              src={typeof photo === 'string' ? photo : URL.createObjectURL(photo)}
              alt={`${title} ${idx + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>

      {selectedPhoto && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[100] p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh]">
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute -top-12 right-0 text-white text-4xl hover:text-gray-300"
            >
              ×
            </button>
            <img
              src={typeof selectedPhoto === 'string' ? selectedPhoto : URL.createObjectURL(selectedPhoto)}
              alt="Full size"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </div>
  );
};

const StaffDashboard = () => {
  const [user, setUser] = useState(null);
  const eventId = user?.event || getEvent();
  const [dailyComment, setDailyComment] = useState("");
  const [dailyPhotos, setDailyPhotos] = useState([]);
  const [submittingUpdate, setSubmittingUpdate] = useState(false);

  // Navigation state
  const [activeView, setActiveView] = useState("marking");

  // Marking states
  const [stands, setStands] = useState([]);
  const [selectedStand, setSelectedStand] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Check-in states
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [savingCheckIn, setSavingCheckIn] = useState(false);
  const [checkInFormData, setCheckInFormData] = useState({
    contractor: {
      companyName: "",
      chiefName: "",
      phone: "",
      email: "",
    },
  });

  // Ticket states
  const [assignedTickets, setAssignedTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [message, setMessage] = useState("");
  const [messagePhotos, setMessagePhotos] = useState([]);
  const [closePhotos, setClosePhotos] = useState([]);

  const fetchStands = async () => {
    try {
      const res = await axiosInstance.get(`/stand/${eventId}`);
      setStands(res.data.stands || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchUser = async () => {
    try {
      const res = await axiosInstance.get(`/auth/user/${userId}`);
      setUser(res.data.user);
    } catch (error) {
      console.error(error);
    }
  };


  const fetchActivities = async (standId) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/activity/stand/${standId}`);
      setActivities(res.data.activities || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedTickets = async () => {
    try {
      const res = await axiosInstance.get("/ticket");
      const assigned = Array.isArray(res.data.assignedTickets) ? res.data.assignedTickets : [];
      setAssignedTickets(assigned);
    } catch (error) {
      console.error("Fetch tickets error", error);
      setAssignedTickets([]);
    }
  };

  const fetchTicketById = async (id) => {
    try {
      const res = await axiosInstance.get(`/ticket/${id}`);
      const ticketData = res.data.ticket || res.data;
      setSelectedTicket(ticketData);
    } catch (error) {
      console.error("Fetch ticket error", error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const toggleStatus = async (activity) => {
    try {
      const res = await axiosInstance.put(`/activity/mark/${activity._id}`);
      const updatedActivity = res.data.data;
      setActivities((prev) =>
        prev.map((a) => (a._id === updatedActivity._id ? updatedActivity : a))
      );
    } catch (error) {
      console.error(error);
    }
  };

  const submitDailyUpdate = async () => {
    if (!dailyComment && dailyPhotos.length === 0) {
      alert("Add a comment or at least one photo");
      return;
    }

    try {
      setSubmittingUpdate(true);

      const formData = new FormData();
      formData.append("comment", dailyComment);
      dailyPhotos.forEach(file => formData.append("photos", file));

      await axiosInstance.post(`/stand/daily-update/${selectedStand._id}`, formData);

      alert("Daily update added ✅");

      setDailyComment("");
      setDailyPhotos([]);

      const res = await axiosInstance.get(`/stand/${selectedStand._id}`);
      setSelectedStand(res.data.data);

    } catch (err) {
      console.error(err);
      alert("Failed to add update");
    } finally {
      setSubmittingUpdate(false);
    }
  };

  const handleStandClick = (stand) => {

    if (!canWorkWithStand(stand)) {
      toast.error("You can only work with stands in your assigned halls.");
      return;
    }

    if (stand.plan?.status !== "validated") {
      alert("Cannot access this stand. The plan must be validated first.");
      return;
    }

    if (!stand.onsite?.arrivalDate) {
      setSelectedStand(stand);
      setShowCheckInModal(true);
      setCheckInFormData({
        contractor: {
          companyName: stand.onsite?.contractor?.companyName || "",
          chiefName: stand.onsite?.contractor?.chiefName || "",
          phone: stand.onsite?.contractor?.phone || "",
          email: stand.onsite?.contractor?.email || "",
        },
      });
      return;
    }

    setSelectedStand(stand);
    fetchActivities(stand._id);
    setShowActivityModal(true);
  };

  const closeActivityModal = () => {
    setShowActivityModal(false);
    setSelectedStand(null);
    setActivities([]);
  };

  const handleCheckInChange = (e) => {
    const { name, value } = e.target;

    if (name.startsWith("contractor.")) {
      const key = name.split(".")[1];
      setCheckInFormData((prev) => ({
        ...prev,
        contractor: { ...prev.contractor, [key]: value },
      }));
    }
  };

  const handleSaveCheckIn = async () => {
    setSavingCheckIn(true);
    console.log(selectedStand);

    try {
      await axiosInstance.put(`/stand/${selectedStand._id}`, {
        onsite: {
          ...checkInFormData,
          arrivalDate: new Date().toISOString(),
        },
      });

      const updatedStandRes = await axiosInstance.get(`/stand/${selectedStand._id}`);
      const updatedStand = updatedStandRes.data.data;

      const activitiesRes = await axiosInstance.get(`/activity/stand/${selectedStand._id}`);
      const standActivities = activitiesRes.data.activities || [];

      const arrivalActivity = standActivities.find(
        act => act.name?.toLowerCase().includes("arrival") ||
          act.name?.toLowerCase().includes("arrivée")
      );

      if (arrivalActivity && arrivalActivity.status !== "done") {
        await axiosInstance.put(`/activity/mark/${arrivalActivity._id}`);
      }

      toast.success("Check-in recorded successfully!");
      setShowCheckInModal(false);

      fetchStands();

      setSelectedStand(updatedStand);
      fetchActivities(updatedStand._id);
      setShowActivityModal(true);

    } catch (error) {
      console.error(error);
    } finally {
      setSavingCheckIn(false);
    }
  };

  const sendMessage = async () => {
    try {
      if (!message && !messagePhotos.length) {
        alert("Message or photo required");
        return;
      }

      const formData = new FormData();
      formData.append("message", message);
      messagePhotos.forEach((file) => formData.append("images", file));

      await axiosInstance.post(`/ticket/message/${selectedTicket._id}`, formData);

      setMessage("");
      setMessagePhotos([]);
      fetchTicketById(selectedTicket._id);
      alert("Message sent");
    } catch (error) {
      console.error("Send message error", error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const closeTicket = async () => {
    if (closePhotos.length === 0) {
      alert("Closing photo required");
      return;
    }

    try {
      const formData = new FormData();
      closePhotos.forEach(file => formData.append("photos", file));

      await axiosInstance.put(`/ticket/close/${selectedTicket._id}`, formData);

      setClosePhotos([]);
      fetchTicketById(selectedTicket._id);
      fetchAssignedTickets();
      alert("Ticket closed successfully");
    } catch (error) {
      console.error("Close ticket error", error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const formatArrivalDate = (date) => {
    if (!date) return null;
    return new Date(date).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'done':
        return 'bg-emerald-100 text-emerald-700';
      case 'pending':
        return 'bg-amber-100 text-amber-700';
      case 'delayed':
        return 'bg-rose-100 text-rose-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'done':
        return '✓';
      case 'pending':
        return '⏱';
      case 'delayed':
        return '⚠';
      default:
        return '○';
    }
  };

  const getTicketStatusColor = (status) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "in_progress": return "bg-blue-100 text-blue-800 border-blue-300";
      case "resolved": return "bg-green-100 text-green-800 border-green-300";
      case "rejected": return "bg-red-100 text-red-800 border-red-300";
      default: return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const getPriorityColor = (priorite) => {
    switch (priorite) {
      case "critical": return "bg-red-500 text-white";
      case "high": return "bg-orange-500 text-white";
      case "medium": return "bg-yellow-500 text-white";
      case "low": return "bg-green-500 text-white";
      default: return "bg-gray-500 text-white";
    }
  };

  const renderTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const canAccessStand = (stand) => {
    return stand.plan?.status === "validated";
  };

  // Check if user can work with this stand (for floor managers, check hall assignment)
  const canWorkWithStand = (stand) => {
    // If not a floor manager, can work with all stands
    if (user?.role !== "floor-manager") {
      return true;
    }

    // If floor manager, can only work with stands in their halls
    if (!user.halls || user.halls.length === 0) {
      return false;
    }

    // Check if stand's hall is in user's halls
    return user.halls.includes(stand.hall);
  };

  const getStandStatus = (stand) => {
    if (stand.plan?.status !== "validated") {
      return { text: "Plan Not Validated", color: "bg-red-100 text-red-700", icon: "⚠" };
    }
    if (!stand.onsite?.arrivalDate) {
      return { text: "Pending Check-In", color: "bg-amber-100 text-amber-700", icon: "📍" };
    }
    return { text: "Checked In", color: "bg-green-100 text-green-700", icon: "✓" };
  };

  // ==================== FILTERED DATA ====================

  const filteredStands = stands.filter(stand => {
    const searchLower = searchTerm.toLowerCase();
    return (
      (stand.name || '').toLowerCase().includes(searchLower) ||
      (stand.hall || '').toLowerCase().includes(searchLower) ||
      (stand.standNumber || '').toLowerCase().includes(searchLower) ||
      (stand.exhibitorName || '').toLowerCase().includes(searchLower) ||
      (stand.tentNumber || '').toLowerCase().includes(searchLower)
    );
  });

  // ==================== EFFECTS ====================

  useEffect(() => {
    fetchUser();
    if (eventId) {
      if (activeView === "marking") {
        fetchStands();
        const interval = setInterval(() => {
          fetchStands();
          if (showActivityModal && selectedStand) {
            fetchActivities(selectedStand._id);
          }
        }, 30000);
        return () => clearInterval(interval);
      } else if (activeView === "cleaning") {
        fetchAssignedTickets();
        const interval = setInterval(() => {
          fetchAssignedTickets();
        }, 30000);
        return () => clearInterval(interval);
      }
    }
  }, [eventId, activeView, showActivityModal, selectedStand]);

  // ==================== RENDER ====================

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-pink-50">
      {/* Top Navigation */}
      <div className="bg-white shadow-lg border-b-4 border-indigo-500">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">
                Staff Dashboard
              </h1>
              <p className="text-sm text-slate-500 mt-1">Manage activities and tickets</p>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="bg-gradient-to-r from-indigo-100 to-blue-100 px-4 py-2 rounded-full">
                <span className="font-bold text-indigo-700">{user?.name || "Staff Member"}</span>
              </div>
              {user?.role === "floor-manager" && user?.halls?.length > 0 && (
                <div className="bg-gradient-to-r from-purple-100 to-pink-100 px-4 py-2 rounded-full">
                  <span className="font-medium text-purple-700">
                    Halls: {user.halls.join(", ")}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setActiveView("marking")}
              className={`flex-1 py-3 px-6 rounded-xl font-bold transition-all transform hover:scale-105 ${activeView === "marking"
                ? "bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-lg"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
            >
              <div className="flex items-center justify-center gap-2">
                <span className="text-xl">✓</span>
                <span>Activity Marking</span>
              </div>
            </button>
            <button
              onClick={() => setActiveView("cleaning")}
              className={`flex-1 py-3 px-6 rounded-xl font-bold transition-all transform hover:scale-105 ${activeView === "cleaning"
                ? "bg-gradient-to-r from-cyan-600 to-blue-600 text-white shadow-lg"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
            >
              <div className="flex items-center justify-center gap-2">
                <span className="text-xl">🎫</span>
                <span>
                  Tickets ({assignedTickets.filter(t => t.status !== "resolved").length})
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto p-6">
        {/* ==================== MARKING VIEW ==================== */}
        {activeView === "marking" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-slate-900">Stand Activities Management</h2>
              <div className="text-sm text-slate-500 bg-white px-4 py-2 rounded-full font-medium shadow">
                {filteredStands.length} {filteredStands.length === 1 ? 'Stand' : 'Stands'}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-4">
              <div className="relative">
                <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by hall, stand number, exhibitor name, tent number..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <MdClose className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Hall</th>
                      <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Stand Number</th>
                      <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Exhibitor Name</th>
                      <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Size</th>
                      <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Tent Number</th>
                      <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Progress</th>
                      <th className="px-6 py-4 text-left text-sm font-bold uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {filteredStands.map((stand) => {
                      const status = getStandStatus(stand);
                      const accessible = canAccessStand(stand);
                      const canWork = canWorkWithStand(stand);

                      return (
                        <tr
                          key={stand._id}
                          className={`transition-colors ${!canWork ? 'bg-slate-50 opacity-60' : 'hover:bg-slate-50'
                            }`}
                        >
                          <td className="px-6 py-4 text-sm font-medium text-slate-900">
                            {stand.hall || 'N/A'}
                            {!canWork && user?.role === "floor-manager" && (
                              <span className="ml-2 text-xs text-slate-500">🔒</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm font-bold text-indigo-600">{stand.standNumber || stand.name}</td>
                          <td className="px-6 py-4 text-sm text-slate-700">{stand.exhibitorName || stand.name}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{stand.size || 'N/A'}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{stand.tentNumber || 'N/A'}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${status.color}`}>
                              <span>{status.icon}</span>
                              {status.text}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {accessible ? (
                              <div className="flex items-center gap-3">
                                <div className="flex-1 bg-slate-200 rounded-full h-2 overflow-hidden">
                                  <div
                                    className="bg-gradient-to-r from-indigo-600 to-blue-600 h-full transition-all duration-500"
                                    style={{ width: `${stand.progress?.toFixed(2) || 0}%` }}
                                  />
                                </div>
                                <span className="text-sm font-bold text-slate-900 min-w-[3rem]">
                                  {stand.progress?.toFixed(2) || 0}%
                                </span>
                              </div>
                            ) : (
                              <span className="text-sm text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleStandClick(stand)}
                              disabled={!accessible || !canWork}
                              className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${accessible && canWork
                                ? stand.onsite?.arrivalDate
                                  ? "bg-gradient-to-r from-indigo-600 to-blue-600 text-white hover:shadow-lg hover:scale-105"
                                  : "bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-lg hover:scale-105"
                                : "bg-slate-300 text-slate-500 cursor-not-allowed"
                                }`}
                              title={!canWork && user?.role === "floor-manager" ? "Not in your assigned halls" : ""}
                            >
                              {!accessible
                                ? "Plan Required"
                                : !canWork
                                  ? "Not Your Hall"
                                  : stand.onsite?.arrivalDate
                                    ? "View Activities"
                                    : "Check In"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredStands.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  {searchTerm ? `No stands found matching "${searchTerm}"` : 'No stands found'}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== TICKETS VIEW ==================== */}
        {activeView === "cleaning" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Assigned Tickets</h2>

              <div className="space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto">
                {!Array.isArray(assignedTickets) || assignedTickets.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-slate-500">No tickets assigned to you</p>
                  </div>
                ) : (
                  assignedTickets.map((ticket) => (
                    <div
                      key={ticket._id}
                      onClick={() => fetchTicketById(ticket._id)}
                      className={`border-2 p-4 rounded-xl cursor-pointer transition-all hover:shadow-lg ${selectedTicket?._id === ticket._id
                        ? "border-cyan-500 bg-gradient-to-br from-cyan-50 to-blue-100 shadow-md"
                        : "border-slate-200 hover:border-cyan-300 bg-white"
                        }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <p className="font-bold text-slate-800 text-lg mb-1">{ticket.stand}</p>
                          <p className="text-sm text-slate-600">{ticket.location || "No location"}</p>
                        </div>
                        <span className={`text-xs px-3 py-1 rounded-full font-bold shadow-sm ${getPriorityColor(ticket.priorite)}`}>
                          {ticket.priorite}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 mb-3 line-clamp-2">{ticket.description}</p>
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-xs px-3 py-1.5 rounded-full font-semibold border ${getTicketStatusColor(ticket.status)}`}>
                          {ticket.status.replace("_", " ")}
                        </span>
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          {ticket.photos && ticket.photos.length > 0 && (
                            <span className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-full">
                              📷 {ticket.photos.length}
                            </span>
                          )}
                          <span className="bg-slate-100 px-2 py-1 rounded-full">
                            {new Date(ticket.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border border-slate-200">
              {!selectedTicket ? (
                <div className="flex flex-col items-center justify-center h-full text-slate-400">
                  <span className="text-6xl mb-4">🎫</span>
                  <p className="text-lg">Select a ticket to view details</p>
                </div>
              ) : (
                <div className="flex flex-col h-full overflow-y-auto">
                  <div className="flex items-start justify-between mb-6 sticky top-0 bg-white pb-3 border-b">
                    <div>
                      <h2 className="font-bold text-2xl text-slate-800 mb-1">{selectedTicket.stand}</h2>
                      <p className="text-sm text-slate-500">
                        Created {new Date(selectedTicket.createdAt).toLocaleString()}
                      </p>
                    </div>
                    <span className={`text-xs px-4 py-2 rounded-full font-bold shadow-sm ${getPriorityColor(selectedTicket.priorite)}`}>
                      {selectedTicket.priorite}
                    </span>
                  </div>

                  <div className="space-y-4 mb-4">
                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4 rounded-xl border border-slate-200">
                      <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Status</p>
                      <span className={`inline-block text-sm px-4 py-2 rounded-full font-semibold border shadow-sm ${getTicketStatusColor(selectedTicket.status)}`}>
                        {selectedTicket.status.replace("_", " ")}
                      </span>
                    </div>

                    {selectedTicket.location && (
                      <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4 rounded-xl border border-slate-200">
                        <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Location</p>
                        <p className="text-sm text-slate-800 font-medium">{selectedTicket.location}</p>
                      </div>
                    )}

                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4 rounded-xl border border-slate-200">
                      <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wide">Description</p>
                      <p className="text-sm text-slate-800 leading-relaxed">{selectedTicket.description}</p>
                    </div>

                    {selectedTicket.photos && selectedTicket.photos.length > 0 && (
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                        <PhotoGallery photos={selectedTicket.photos} title="Ticket Photos" />
                      </div>
                    )}

                    {selectedTicket.closingPhotos && selectedTicket.closingPhotos.length > 0 && (
                      <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl border border-green-200">
                        <PhotoGallery photos={selectedTicket.closingPhotos} title="Closing Photos" />
                      </div>
                    )}
                  </div>

                  <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 mb-4 border border-slate-200">
                    <p className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Messages</p>
                    {selectedTicket.messages && Array.isArray(selectedTicket.messages) && selectedTicket.messages.length > 0 ? (
                      <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                        {selectedTicket.messages.map((msg, idx) => (
                          <div key={idx} className="bg-white p-3 rounded-lg shadow-sm border border-slate-200">
                            <p className="text-sm text-slate-800 mb-2">{msg.text || msg.message}</p>
                            {msg.images && msg.images.length > 0 && (
                              <PhotoGallery photos={msg.images} title="" />
                            )}
                            <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                              <span>🕒</span>
                              {new Date(msg.timestamp || msg.createdAt).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-4">No messages yet</p>
                    )}
                  </div>

                  {selectedTicket.status !== "resolved" && (
                    <div className="space-y-4 bg-white border-t-2 border-slate-200 pt-4">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                          Add Message
                        </label>
                        <textarea
                          className="w-full border-2 border-slate-300 p-3 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition"
                          placeholder="Type your message here..."
                          rows="3"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                          Attach Photos
                        </label>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          className="w-full text-sm text-slate-500 file:mr-4 file:py-3 file:px-6 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition cursor-pointer"
                          onChange={(e) => setMessagePhotos([...e.target.files])}
                        />
                        {messagePhotos.length > 0 && (
                          <div className="mt-2">
                            <PhotoGallery photos={messagePhotos} title={`${messagePhotos.length} photo(s) selected`} />
                          </div>
                        )}
                      </div>

                      <button
                        onClick={sendMessage}
                        className="w-full bg-gradient-to-r from-cyan-600 to-cyan-700 text-white py-3 rounded-lg font-bold hover:from-cyan-700 hover:to-cyan-800 transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                      >
                        📤 Send Message
                      </button>

                      <div className="border-t-2 border-slate-200 pt-4">
                        <label className="block text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">
                          Close Ticket
                        </label>
                        <p className="text-xs text-slate-500 mb-3">
                          Upload photos showing the completed work to close this ticket
                        </p>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          className="w-full text-sm text-slate-500 file:mr-4 file:py-3 file:px-6 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100 transition cursor-pointer mb-3"
                          onChange={(e) => setClosePhotos([...e.target.files])}
                        />
                        {closePhotos.length > 0 && (
                          <div className="mb-3">
                            <PhotoGallery photos={closePhotos} title={`${closePhotos.length} closing photo(s) selected`} />
                          </div>
                        )}
                        <button
                          onClick={closeTicket}
                          disabled={closePhotos.length === 0}
                          className={`w-full py-3 rounded-lg font-bold transition-all shadow-md hover:shadow-lg transform hover:scale-[1.02] ${closePhotos.length > 0
                            ? 'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                        >
                          ✅ Close Ticket
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ==================== CHECK-IN MODAL ==================== */}
      {showCheckInModal && selectedStand && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold">Arrival Check-In</h3>
                  <p className="text-green-200 text-sm mt-0.5">
                    Stand {selectedStand.standNumber || selectedStand.tentNumber}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowCheckInModal(false);
                    setSelectedStand(null);
                  }}
                  className="p-1 hover:bg-white/20 rounded-lg transition"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-5">
              {/* Stand Info */}
              <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 p-4 rounded-xl">
                <div>
                  <span className="text-slate-500 text-xs">Hall</span>
                  <p className="font-medium text-slate-800">{selectedStand.hall || "—"}</p>
                </div>
                <div>
                  <span className="text-slate-500 text-xs">Stand Number</span>
                  <p className="font-medium text-slate-800">{selectedStand.standNumber || "—"}</p>
                </div>
                <div>
                  <span className="text-slate-500 text-xs">Tent Number</span>
                  <p className="font-medium text-slate-800">{selectedStand.tentNumber || "—"}</p>
                </div>
                <div>
                  <span className="text-slate-500 text-xs">Exhibitor</span>
                  <p className="font-medium text-slate-800">{selectedStand.exhibitorName || "—"}</p>
                </div>
              </div>

              {/* Contractor Details */}
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Contractor Details
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Company Name</label>
                    <input
                      name="contractor.companyName"
                      placeholder="Enter company name"
                      value={checkInFormData.contractor.companyName}
                      onChange={handleCheckInChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Chief Name</label>
                    <input
                      name="contractor.chiefName"
                      placeholder="Enter chief name"
                      value={checkInFormData.contractor.chiefName}
                      onChange={handleCheckInChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Phone Number</label>
                    <input
                      name="contractor.phone"
                      placeholder="Enter phone number"
                      value={checkInFormData.contractor.phone}
                      onChange={handleCheckInChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Email</label>
                    <input
                      name="contractor.email"
                      placeholder="Enter email"
                      value={checkInFormData.contractor.email}
                      onChange={handleCheckInChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Timestamp Info */}
              <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded-lg">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Current time will be recorded as arrival time and "Arrival on site" activity will be marked as done
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-slate-50 border-t flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCheckInModal(false);
                  setSelectedStand(null);
                }}
                className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 text-sm font-medium transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCheckIn}
                disabled={savingCheckIn}
                className="px-5 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg shadow-sm hover:from-green-700 hover:to-emerald-700 text-sm font-medium transition flex items-center gap-2 disabled:opacity-50"
              >
                {savingCheckIn ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Confirm Check-In
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== ACTIVITY MODAL ==================== */}
      {showActivityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">{selectedStand?.name}</h2>
                <p className="text-indigo-100 text-sm mt-1">
                  {selectedStand?.exhibitorName || 'Exhibitor'} • {selectedStand?.standNumber || 'Stand'}
                </p>
                {selectedStand?.onsite?.arrivalDate && (
                  <p className="text-indigo-200 text-xs mt-1">
                    ✓ Checked in: {formatArrivalDate(selectedStand.onsite.arrivalDate)}
                  </p>
                )}
              </div>
              <button
                onClick={closeActivityModal}
                className="w-10 h-10 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 flex items-center justify-center transition-all"
              >
                <MdClose className="text-2xl" />
              </button>
            </div>

            {/* Main Content */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  No activities found for this stand
                </div>
              ) : (
                <div className="p-6 space-y-6">
                  {/* Stand Plan Viewer */}
                  {selectedStand?.plan?.file && (
                    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200 rounded-xl p-5">
                      <StandPlanViewer plan={selectedStand.plan} />
                    </div>
                  )}

                  <div>
                    <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                      Activities
                    </h3>
                    <div className="space-y-3">
                      {activities.map((activity, index) => (
                        <div key={activity._id} className="group relative">
                          {index < activities.length - 1 && (
                            <div className="absolute left-6 top-16 w-0.5 h-6 bg-slate-200" />
                          )}

                          <div className="bg-white rounded-xl p-4 border-2 border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all">
                            <div className="flex items-center gap-4">
                              <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold shadow-sm transition-transform group-hover:scale-105 ${getStatusStyle(activity.status)}`}>
                                {getStatusIcon(activity.status)}
                              </div>

                              <div className="flex-1">
                                <h4 className="text-base font-bold text-slate-900 mb-1">{activity.name}</h4>
                                <div className="text-xs text-slate-500">
                                  {renderTimestamp(activity.markedAt)}
                                </div>
                              </div>

                              <div className="flex items-center gap-4">
                                <div className="relative w-14 h-14">
                                  <svg className="w-14 h-14 transform -rotate-90">
                                    <circle cx="28" cy="28" r="24" stroke="currentColor" strokeWidth="5" fill="none" className="text-slate-200" />
                                    <circle
                                      cx="28"
                                      cy="28"
                                      r="24"
                                      stroke="currentColor"
                                      strokeWidth="5"
                                      fill="none"
                                      strokeDasharray={`${2 * Math.PI * 24}`}
                                      strokeDashoffset={`${2 * Math.PI * 24 * (1 - activity.percent / 100)}`}
                                      className={`transition-all ${activity.status === 'done' ? 'text-emerald-500' :
                                        activity.status === 'pending' ? 'text-amber-500' :
                                          activity.status === 'delayed' ? 'text-rose-500' : 'text-slate-400'
                                        }`}
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-xs font-bold text-slate-900">{activity.percent}%</span>
                                  </div>
                                </div>

                                <button
                                  onClick={() => toggleStatus(activity)}
                                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all hover:scale-105 ${getStatusStyle(activity.status)}`}
                                >
                                  {activity.status === "done" ? (
                                    <>
                                      <MdCheckCircle className="text-lg" />
                                      Done
                                    </>
                                  ) : (
                                    <>
                                      <MdRadioButtonUnchecked className="text-lg" />
                                      Mark
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {(selectedStand?.electricity || selectedStand?.internet) && (
                    <div className="bg-white border-2 border-slate-200 rounded-xl p-6 shadow-sm">
                      <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                        <MdBolt className="text-yellow-500" />
                        Technical Requirements
                      </h3>

                      <div className="grid md:grid-cols-2 gap-8">

                        {/* Electricity Summary */}
                        {selectedStand?.electricity && (
                          <div className="space-y-3">
                            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                              <MdBolt className="text-yellow-500" /> Electricity
                            </h4>

                            <div className="text-sm text-slate-600 space-y-1">
                              <p><span className="font-medium text-slate-800">Amperage:</span> {selectedStand.electricity.amperage || "—"} A</p>
                              <p><span className="font-medium text-slate-800">Lines / Circuits:</span> {selectedStand.electricity.linesCount || "—"}</p>
                              <p><span className="font-medium text-slate-800">Phase:</span> {selectedStand.electricity.phase || "—"}</p>
                              <p>
                                <span className="font-medium text-slate-800">24/7 Power:</span>{" "}
                                {selectedStand.electricity.need24h ? "Yes" : "No"}
                              </p>

                              {selectedStand.electricity.comment && (
                                <p className="pt-2 text-slate-500 italic">
                                  “{selectedStand.electricity.comment}”
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Internet Summary */}
                        {selectedStand?.internet?.needed && (
                          <div className="space-y-3">
                            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                              <MdWifi className="text-indigo-500" /> Internet
                            </h4>

                            <div className="text-sm text-slate-600 space-y-1">
                              <p><span className="font-medium text-slate-800">Type:</span> {selectedStand.internet.type || "—"}</p>
                              <p><span className="font-medium text-slate-800">Speed:</span> {selectedStand.internet.speedMbps || "—"} Mbps</p>

                              {selectedStand.internet.type !== "wifi" && (
                                <p><span className="font-medium text-slate-800">RJ45 Ports:</span> {selectedStand.internet.rj45Count || "—"}</p>
                              )}

                              {selectedStand.internet.comment && (
                                <p className="pt-2 text-slate-500 italic">
                                  “{selectedStand.internet.comment}”
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="border-t-2 border-slate-200 pt-6">
                    <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                      <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Daily Updates
                    </h3>

                    {selectedStand?.dailyUpdates?.length > 0 && (
                      <div className="space-y-3 mb-6">
                        {selectedStand.dailyUpdates.slice().reverse().map((update, idx) => (
                          <div key={idx} className="bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-slate-200 rounded-xl p-4 hover:shadow-md transition-all">
                            <p className="text-sm text-slate-800 leading-relaxed mb-3">{update.comment}</p>

                            {update.photos?.length > 0 && (
                              <PhotoGallery photos={update.photos} />
                            )}

                            <div className="flex items-center gap-2 text-xs text-slate-500 mt-3 pt-3 border-t border-slate-300">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {new Date(update.date).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add New Update Form */}
                    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-200 rounded-xl p-5 shadow-sm">
                      <h4 className="text-sm font-bold text-slate-700 mb-4 uppercase tracking-wide">Add New Update</h4>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-2">
                            Progress Notes
                          </label>
                          <textarea
                            placeholder="Describe today's progress, challenges, or observations..."
                            value={dailyComment}
                            onChange={(e) => setDailyComment(e.target.value)}
                            className="w-full border-2 border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all resize-none"
                            rows={4}
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-2">
                            Attach Photos
                          </label>
                          <div className="relative">
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={(e) => setDailyPhotos([...e.target.files])}
                              className="hidden"
                              id="daily-photo-upload"
                            />
                            <label
                              htmlFor="daily-photo-upload"
                              className="flex items-center justify-center gap-2 w-full border-2 border-dashed border-slate-300 rounded-lg p-4 cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-all group"
                            >
                              <svg className="w-6 h-6 text-slate-400 group-hover:text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span className="text-sm font-medium text-slate-600 group-hover:text-indigo-700">
                                {dailyPhotos.length > 0 ? `${dailyPhotos.length} photo(s) selected` : 'Click to upload photos'}
                              </span>
                            </label>
                          </div>

                          {dailyPhotos.length > 0 && (
                            <div className="mt-3">
                              <PhotoGallery photos={dailyPhotos} />
                            </div>
                          )}
                        </div>

                        <button
                          onClick={submitDailyUpdate}
                          disabled={submittingUpdate || (!dailyComment && dailyPhotos.length === 0)}
                          className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-3 rounded-lg font-bold hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                        >
                          {submittingUpdate ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                              Add Update
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t-2 border-slate-200 p-5 bg-slate-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  <span className="font-bold text-slate-900">
                    {activities.filter(a => a.status === 'done').length} / {activities.length}
                  </span>
                  <span>completed</span>
                </div>
                <button
                  onClick={closeActivityModal}
                  className="px-6 py-2.5 bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-800 transition-all shadow-md hover:shadow-lg"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffDashboard;